import React, { useState } from 'react';

import { Popover } from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { pt } from 'date-fns/locale';

import { formatDatePtBr, normalizeDayStart } from '../utils/dateUtils';

import './dayRangePicker.css';

const getDateTimestamp = (date) => date ? normalizeDayStart(date).getTime() : null;

const RangePreviewDay = ({
    day,
    rangeEnd,
    rangeStart,
    previewEnd,
    onPreviewChange,
    ...dayProps
}) => {
    const dayTimestamp = getDateTimestamp(day);
    const startTimestamp = getDateTimestamp(rangeStart);
    const selectedEndTimestamp = getDateTimestamp(rangeEnd);
    const previewEndTimestamp = getDateTimestamp(previewEnd);
    const effectiveEndTimestamp = selectedEndTimestamp ??
        (previewEndTimestamp !== null && previewEndTimestamp >= startTimestamp ? previewEndTimestamp : null);
    const isRangeStart = startTimestamp !== null && dayTimestamp === startTimestamp;
    const isRangeEnd = effectiveEndTimestamp !== null && dayTimestamp === effectiveEndTimestamp;
    const isInRange = startTimestamp !== null &&
        effectiveEndTimestamp !== null &&
        dayTimestamp >= startTimestamp &&
        dayTimestamp <= effectiveEndTimestamp;
    const isPreview = selectedEndTimestamp === null && isInRange;
    const classNames = [
        'day-range-picker-day',
        isInRange ? 'is-in-range' : '',
        isPreview ? 'is-preview' : '',
        isRangeStart ? 'is-range-start' : '',
        isRangeEnd ? 'is-range-end' : ''
    ].filter(Boolean).join(' ');

    return (
        <PickersDay
            {...dayProps}
            day={day}
            className={classNames}
            onMouseEnter={() => onPreviewChange(day)}
            onMouseLeave={() => onPreviewChange(null)}
        />
    );
};

const DayRangePicker = ({
    ariaLabel = 'Selecionar período',
    disabled = false,
    endDate,
    maxDate = new Date(),
    minDate,
    onApply,
    onClear,
    startDate
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [draftStartDate, setDraftStartDate] = useState(startDate);
    const [draftEndDate, setDraftEndDate] = useState(endDate);
    const [previewEndDate, setPreviewEndDate] = useState(null);
    const isOpen = Boolean(anchorEl);
    const hasAppliedRange = Boolean(startDate && endDate);
    const hasCompleteDraftRange = Boolean(draftStartDate && draftEndDate);
    const rangeLabel = hasAppliedRange
        ? `${formatDatePtBr(startDate)} - ${formatDatePtBr(endDate)}`
        : 'Selecionar período';

    const handleOpen = (event) => {
        setDraftStartDate(startDate);
        setDraftEndDate(endDate);
        setPreviewEndDate(null);
        setAnchorEl(event.currentTarget);
    };

    const handleCalendarChange = (date) => {
        if (!date) return;

        if (!draftStartDate || draftEndDate) {
            setDraftStartDate(date);
            setDraftEndDate(null);
            setPreviewEndDate(null);
            return;
        }

        if (date.getTime() < draftStartDate.getTime()) {
            setDraftStartDate(date);
            setPreviewEndDate(null);
            return;
        }

        setDraftEndDate(date);
        setPreviewEndDate(null);
    };

    const handleApply = () => {
        if (!hasCompleteDraftRange) return;
        onApply(draftStartDate, draftEndDate);
        setAnchorEl(null);
    };

    const handleClear = () => {
        setDraftStartDate(null);
        setDraftEndDate(null);
        onClear();
        setAnchorEl(null);
    };

    return (
        <>
            <button
                type='button'
                className={`day-range-picker-trigger ${hasAppliedRange ? 'has-value' : ''}`}
                aria-label={ariaLabel}
                aria-expanded={isOpen}
                disabled={disabled}
                onClick={handleOpen}
            >
                <span>{rangeLabel}</span>
                <CalendarMonthRoundedIcon fontSize='inherit' />
            </button>

            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: 'day-range-picker-popover' }}
            >
                <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                    <div className='day-range-picker'>
                        <div className='day-range-picker-heading'>
                            <strong>
                                {!draftStartDate
                                    ? 'Selecione a data inicial'
                                    : !draftEndDate ? 'Selecione a data final' : 'Período selecionado'}
                            </strong>
                            <span>
                                {draftStartDate ? formatDatePtBr(draftStartDate) : 'Início'}
                                {' - '}
                                {draftEndDate ? formatDatePtBr(draftEndDate) : 'Fim'}
                            </span>
                        </div>
                        <DateCalendar
                            value={draftEndDate ?? draftStartDate}
                            onChange={handleCalendarChange}
                            maxDate={maxDate}
                            minDate={minDate}
                            slots={{ day: RangePreviewDay }}
                            slotProps={{
                                day: {
                                    rangeStart: draftStartDate,
                                    rangeEnd: draftEndDate,
                                    previewEnd: previewEndDate,
                                    onPreviewChange: setPreviewEndDate
                                }
                            }}
                        />
                        <div className='day-range-picker-actions'>
                            <button type='button' className='day-range-picker-clear' onClick={handleClear}>
                                Limpar
                            </button>
                            <button
                                type='button'
                                className='day-range-picker-apply'
                                disabled={!hasCompleteDraftRange}
                                onClick={handleApply}
                            >
                                Aplicar
                            </button>
                        </div>
                    </div>
                </LocalizationProvider>
            </Popover>
        </>
    );
};

export default DayRangePicker;
